import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

type SocialPlatform = "instagram" | "facebook" | "youtube" | "x" | "linkedin";

type SocialHandle = {
  platform: SocialPlatform;
  handle: string;
};

const ensureProtocol = (url: string): string => {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

const parseUrl = (
  url: string
): { hostname: string; pathname: string } | null => {
  try {
    const parsed = new URL(ensureProtocol(url));
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const pathname = parsed.pathname.replace(/\/+$/, "");
    return { hostname, pathname };
  } catch {
    return null;
  }
};

export const extractSocialMediaHandle = (url: string): SocialHandle | null => {
  try {
    const parsed = new URL(ensureProtocol(url));
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const pathname = parsed.pathname.replace(/\/+$/, "");

    if (host.includes("instagram.com")) {
      return { platform: "instagram", handle: pathname.split("/")[1] };
    }

    if (host.includes("facebook.com")) {
      return { platform: "facebook", handle: pathname.split("/")[1] };
    }

    if (host.includes("youtube.com")) {
      const segments = pathname.split("/");
      const atHandle = segments.find((seg) => seg.startsWith("@"));
      return atHandle
        ? { platform: "youtube", handle: atHandle.slice(1) }
        : null;
    }

    if (host.includes("twitter.com") || host.includes("x.com")) {
      return { platform: "x", handle: pathname.split("/")[1] };
    }

    if (host.includes("linkedin.com")) {
      const segments = pathname.split("/");
      if (segments[1] === "in" || segments[1] === "company") {
        return { platform: "linkedin", handle: segments[2] };
      }
    }

    return null;
  } catch {
    return null;
  }
};

export const socialMediaConfig = {
  instagram: {
    label: "Instagram",
    name: "instagramUrl" as const,
    baseUrl: "https://www.instagram.com/",
    SvgIcon: InstagramIcon,
    extractHandle: (url: string): string | null => {
      const parsedUrl = parseUrl(url);
      if (parsedUrl && parsedUrl.hostname.includes("instagram.com")) {
        return parsedUrl.pathname.split("/")[1] || null;
      }
      return null;
    },
    handleToUrl: (handle: string): string => {
      return `https://www.instagram.com/${handle}`;
    },
  },
  facebook: {
    label: "Facebook",
    name: "facebookUrl" as const,
    baseUrl: "https://www.facebook.com/",
    SvgIcon: FacebookIcon,
    extractHandle: (url: string): string | null => {
      const parsedUrl = parseUrl(url);
      if (parsedUrl && parsedUrl.hostname.includes("facebook.com")) {
        return parsedUrl.pathname.split("/")[1] || null;
      }
      return null;
    },
    handleToUrl: (handle: string): string => {
      return `https://www.facebook.com/${handle}`;
    },
  },
  x: {
    label: "X",
    name: "xUrl" as const,
    baseUrl: "https://www.x.com/",
    SvgIcon: XIcon,
    extractHandle: (url: string): string | null => {
      const parsedUrl = parseUrl(url);
      if (
        parsedUrl &&
        (parsedUrl.hostname.includes("twitter.com") ||
          parsedUrl.hostname.includes("x.com"))
      ) {
        return parsedUrl.pathname.split("/")[1] || null;
      }
      return null;
    },
    handleToUrl: (handle: string): string => {
      return `https://www.x.com/${handle}`;
    },
  },
  youtube: {
    label: "YouTube",
    name: "youtubeUrl" as const,
    baseUrl: "https://www.youtube.com/",
    SvgIcon: YouTubeIcon,
    extractHandle: (url: string): string | null => {
      const parsedUrl = parseUrl(url);
      if (parsedUrl && parsedUrl.hostname.includes("youtube.com")) {
        const segments = parsedUrl.pathname.split("/");
        const atHandle = segments.find((seg) => seg.startsWith("@"));
        return atHandle ? atHandle.slice(1) : null;
      }
      return null;
    },
    handleToUrl: (handle: string): string => {
      return `https://www.youtube.com/@${handle}`;
    },
  },
  linkedin: {
    label: "LinkedIn",
    name: "linkedInUrl" as const,
    baseUrl: "https://www.linkedin.com/in/",
    SvgIcon: LinkedInIcon,
    extractHandle: (url: string): string | null => {
      const parsedUrl = parseUrl(url);
      if (parsedUrl && parsedUrl.hostname.includes("linkedin.com")) {
        const segments = parsedUrl.pathname.split("/");
        if (segments[1] === "in" || segments[1] === "company") {
          return segments[2] || null;
        }
      }
      return null;
    },
    handleToUrl: (handle: string): string => {
      return `https://www.linkedin.com/in/${handle}`;
    },
  },
};
